// ============================================
// Manifest Utilities - Segment Matching & Coordinate Conversion

// ============================================

import { wgs84ToLocal } from './poiUtils';

export interface ManifestSegment {
  segment_index: number;
  view_bounding_volume: {
    min_corner: [number, number, number];
    max_corner: [number, number, number];
  };
  segment_url: string;
  environment_map_url?: string;
}

export interface ManifestData {
  segment_count: number;
  observer_coordinates: [number, number, number];
  segments: ManifestSegment[];
}

/**
 * Fetch and parse manifest JSON
 */
export async function fetchManifest(manifestUrl: string): Promise<ManifestData> {
  console.log('[ManifestUtils] Fetching manifest:', manifestUrl);
  const response = await fetch(manifestUrl);
  const data = await response.json();
  console.log('[ManifestUtils] Loaded manifest with', data.segment_count, 'segments');
  return data;
}

/**
 * Find which segment contains a given local coordinate
 */
export function findSegmentForLocalCoord(
  localCoord: { x: number; y: number; z: number },
  segments: ManifestSegment[]
): number | null {
  for (const segment of segments) {
    const { min_corner, max_corner } = segment.view_bounding_volume;
    
    if (
      localCoord.x >= min_corner[0] && localCoord.x <= max_corner[0] &&
      localCoord.y >= min_corner[1] && localCoord.y <= max_corner[1] &&
      localCoord.z >= min_corner[2] && localCoord.z <= max_corner[2]
    ) {
      return segment.segment_index;
    }
  }
  
  return null;
}

/**
 * Find which segment contains a POI (WGS84 coords)
 */
export function findSegmentForPOI(
  latitude: number,
  longitude: number,
  altitude: number,
  manifest: ManifestData
): number | null {
  const localCoord = wgs84ToLocal(
    latitude,
    longitude,
    altitude,
    manifest.observer_coordinates
  );
  
  return findSegmentForLocalCoord(localCoord, manifest.segments);
}

/**
 * Convert local coordinates back to WGS84
 * (Inverse of wgs84ToLocal)
 */
export function localToWGS84(
  x: number,
  y: number,
  z: number,
  origin: [number, number, number]
): { latitude: number; longitude: number; altitude: number } {
  const [originLat, originLon, originAlt] = origin;

  // Earth radius in meters
  const R = 6371000;
  const latRad = (originLat * Math.PI) / 180;
  
  // Meters per degree at this latitude
  const metersPerDegLat = (Math.PI / 180) * R;
  const metersPerDegLon = (Math.PI / 180) * R * Math.cos(latRad);

  // Convert from local meters back to degrees
  const longitude = originLon + (x / metersPerDegLon);
  const latitude = originLat + (y / metersPerDegLat);
  const altitude = originAlt + z;

  return { latitude, longitude, altitude };
}

/**
 * Calculate distance between two local coordinates
 */
export function localDistance(
  coord1: { x: number; y: number; z: number },
  coord2: { x: number; y: number; z: number }
): number {
  const dx = coord2.x - coord1.x;
  const dy = coord2.y - coord1.y;
  const dz = coord2.z - coord1.z;
  
  return Math.sqrt(dx * dx + dy * dy + dz * dz);
}

/**
 * Check if two positions are in the same segment
 */
export function areInSameSegment(
  pos1: { latitude: number; longitude: number; altitude: number },
  pos2: { latitude: number; longitude: number; altitude: number },
  manifest: ManifestData
): boolean {
  const segment1 = findSegmentForPOI(pos1.latitude, pos1.longitude, pos1.altitude, manifest);
  const segment2 = findSegmentForPOI(pos2.latitude, pos2.longitude, pos2.altitude, manifest);
  
  return segment1 !== null && segment1 === segment2;
}

/**
 * Get all segments within a radius of a position
 */
export function getSegmentsInRadius(
  latitude: number,
  longitude: number,
  altitude: number,
  radiusMeters: number,
  manifest: ManifestData
): number[] {
  const centerLocal = wgs84ToLocal(latitude, longitude, altitude, manifest.observer_coordinates);
  const segmentIndices = new Set<number>();

  for (const segment of manifest.segments) {
    const { min_corner, max_corner } = segment.view_bounding_volume;
    
    // Calculate closest point in bounding box to center
    const closestX = Math.max(min_corner[0], Math.min(centerLocal.x, max_corner[0]));
    const closestY = Math.max(min_corner[1], Math.min(centerLocal.y, max_corner[1]));
    const closestZ = Math.max(min_corner[2], Math.min(centerLocal.z, max_corner[2]));
    
    const distance = localDistance(centerLocal, { x: closestX, y: closestY, z: closestZ });
    
    if (distance <= radiusMeters) {
      segmentIndices.add(segment.segment_index);
    }
  }
  
  return Array.from(segmentIndices);
}

